// Device.jsx - The "this device" tab on the settings screen: install the
// dashboard as an app, and turn on notifications for the phone in your hand.
//
// Both are per device, not per account, which is the thing people get wrong
// about them. Installing on a phone does not install on the bench machine, and
// turning notifications off on one does not turn them off on the other. Every
// piece of wording here says so.
//
// Nothing on this screen is required. The dashboard works identically in a
// browser tab, and with no VAPID keys on the server the notification half says
// so plainly instead of offering a switch that cannot work.
//
// LEGEND
//   1. Install     one tap where the browser allows it, instructions where it does not
//   2. Notify      permission, subscription, and a test that proves the chain
//   3. Device      the tab

import { useCallback, useEffect, useState } from "react";

import { adminApi } from "../lib/api.js";
import { day } from "../lib/format.js";
import { useInstall, isStandalone, pushSupported, pushPermission, currentSubscription, subscribeToPush, unsubscribeFromPush } from "../lib/pwa.js";
import { Button, Panel, Alert, Toggle, useToast, Tag, Spinner } from "../ui/kit.jsx";
import { Download, Upload, Bell, Check, Phone } from "../ui/icons.jsx";

////////// 1. INSTALL //////////

const STEPS = {
  ios: [
    "Open this page in Safari, if you are not already.",
    "Tap the share button, the square with an arrow coming out of it.",
    "Scroll down and tap Add to Home Screen.",
    "Tap Add. The dashboard now opens from your home screen with no address bar.",
  ],
  safari: [
    "In the Safari menu bar, choose File.",
    "Choose Add to Dock.",
    "Give it a name and choose Add.",
  ],
  prompt: [
    "Open the browser menu, the three dots at the end of the address bar.",
    "Choose Install, or Add to Home screen.",
    "Confirm. It opens in its own window from then on.",
  ],
};

function Install() {
  const { canPrompt, installed, platform, promptInstall } = useInstall();
  const toast = useToast();

  if (installed || isStandalone()) {
    return (
      <Panel className="p-5">
        <div className="flex items-start gap-3">
          <Check size={20} className="text-positive shrink-0 mt-0.5" />
          <div>
            <p className="text-[15px] font-semibold text-ink">This device is set up</p>
            <p className="hint mt-1 leading-relaxed">
              You are running the installed dashboard. It opens from the home screen, without an
              address bar, and remembers you between shifts the same as the browser did.
            </p>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel className="p-5">
      <p className="eyebrow">Install</p>
      <h3 className="display display-sm text-ink mt-1.5">Put the dashboard on your home screen</h3>
      <p className="hint mt-2 leading-relaxed measure">
        It is the same dashboard, opened from an icon instead of a tab: full screen, no address bar,
        and one tap from the lock screen. It is also what lets this device receive notifications.
      </p>

      {canPrompt ? (
        <Button
          className="mt-4"
          onClick={async () => {
            const outcome = await promptInstall();
            if (outcome === "accepted") toast.success("Installed. Look for the balance icon on your home screen.");
            else if (outcome === "unavailable") toast.info("Your browser did not offer the install prompt. The steps below still work.");
          }}
        >
          <Download size={16} /> Install now
        </Button>
      ) : (
        <ol className="mt-4 space-y-2.5">
          {STEPS[platform === "unknown" ? "prompt" : platform].map((step, index) => (
            <li key={step} className="flex gap-3 text-[13.5px] text-ink-soft leading-snug">
              <span className="numeral text-[1.1rem] leading-none text-bronze-soft w-4 shrink-0">{index + 1}</span>
              <span>
                {step}
                {platform === "ios" && index === 1 && <Upload size={14} className="inline ml-1.5 -mt-0.5 text-bronze" />}
              </span>
            </li>
          ))}
        </ol>
      )}

      {platform === "ios" && (
        <p className="hint mt-4 pt-4 border-t border-rule-soft leading-relaxed">
          On an iPhone this is not optional if you want notifications. Safari only offers them to a
          site that has been added to the home screen, and only on iOS 16.4 or newer.
        </p>
      )}
    </Panel>
  );
}

////////// 2. NOTIFICATIONS //////////

function Notify() {
  const toast = useToast();
  const [state, setState] = useState({ loading: true, ready: false, publicKey: null, subscribed: false, devices: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const subscription = await currentSubscription();
      const data = await adminApi.push(subscription?.endpoint);
      setState({ loading: false, ...data, endpoint: subscription?.endpoint || null });
    } catch (error) {
      setState({ loading: false, ready: false, error: error.message, devices: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const permission = pushPermission();

  const turnOn = async () => {
    setBusy(true);
    try {
      const subscription = await subscribeToPush(state.publicKey);
      await adminApi.subscribePush({
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        label: describeThisDevice(),
      });
      await load();
      toast.success("Notifications are on for this device");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      const endpoint = (await unsubscribeFromPush()) || state.endpoint;
      if (endpoint) await adminApi.unsubscribePush(endpoint);
      await load();
      toast.info("Notifications are off for this device");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <Panel className="p-5 flex items-center gap-2.5 text-muted text-sm"><Spinner /> Checking this device</Panel>;
  }

  if (!state.ready) {
    return (
      <Panel className="p-5">
        <p className="eyebrow">Notifications</p>
        <h3 className="display display-sm text-ink mt-1.5">Not switched on for this server</h3>
        <p className="hint mt-2 leading-relaxed measure">
          Push notifications need a signing key pair, which nobody has generated yet. Run
          <code className="mx-1 px-1.5 py-0.5 bg-surface text-[12.5px]">npm --prefix server run vapid</code>
          and put the two lines it prints into the server environment. Until then, staff alerts go out
          by email and text exactly as they do now.
        </p>
      </Panel>
    );
  }

  if (!pushSupported()) {
    return (
      <Panel className="p-5">
        <p className="eyebrow">Notifications</p>
        <h3 className="display display-sm text-ink mt-1.5">This browser cannot receive them</h3>
        <p className="hint mt-2 leading-relaxed measure">
          Try a recent Chrome, Edge or Firefox. On an iPhone, add the dashboard to your home screen
          first: Safari only offers notifications to an installed app.
        </p>
      </Panel>
    );
  }

  return (
    <Panel className="p-5 space-y-4">
      <div>
        <p className="eyebrow">Notifications</p>
        <h3 className="display display-sm text-ink mt-1.5">Alerts on this device</h3>
        <p className="hint mt-2 leading-relaxed measure">
          A notification arrives on the lock screen the moment something happens, whether or not the
          dashboard is open. Which events you get is on the alerts tab, and it is the same list that
          controls your email and texts.
        </p>
      </div>

      {permission === "denied" && (
        <Alert tone="warning" title="Notifications are blocked for this site">
          The browser is refusing before we get a chance to ask. Open the site settings, usually the
          icon at the left of the address bar, allow notifications, then come back.
        </Alert>
      )}

      <Toggle
        label={state.subscribed ? "On for this device" : "Off for this device"}
        hint={state.subscribed
          ? "This browser is registered. Turning it off here does not affect your other devices."
          : "Your browser will ask for permission once."}
        checked={state.subscribed}
        disabled={busy || permission === "denied"}
        onChange={(next) => (next ? turnOn() : turnOff())}
      />

      {state.subscribed && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            variant="quiet"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { delivered } = await adminApi.testPush();
                toast.success(`Sent to ${delivered} ${delivered === 1 ? "device" : "devices"}`);
              } catch (error) {
                toast.error(error.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Bell size={15} /> Send a test
          </Button>
        </div>
      )}

      {state.devices?.length > 0 && (
        <div className="pt-4 border-t border-rule-soft">
          <p className="eyebrow mb-2.5">Registered devices</p>
          <ul className="space-y-2">
            {state.devices.map((device) => (
              <li key={`${device.label}-${device.createdAt}`} className="flex items-center justify-between gap-3 text-[13.5px]">
                <span className="flex items-center gap-2 min-w-0">
                  <Phone size={14} className="text-faint shrink-0" />
                  <span className="text-ink truncate">{device.label}</span>
                  {device.thisDevice && <Tag tone="bronze">This one</Tag>}
                </span>
                <span className="hint shrink-0">Added {day(device.createdAt)}</span>
              </li>
            ))}
          </ul>
          <p className="hint mt-3 leading-relaxed">
            A device drops off this list on its own once its browser stops accepting notifications,
            which is what happens when the app is deleted or the permission is revoked.
          </p>
        </div>
      )}
    </Panel>
  );
}

// Something recognisable in the device list. Nobody needs the full user agent,
// and there is no reason to store one.
function describeThisDevice() {
  if (typeof navigator === "undefined") return "A device";
  const agent = navigator.userAgent;
  const platform =
    /iphone/i.test(agent) ? "iPhone" :
    /ipad/i.test(agent) ? "iPad" :
    /android/i.test(agent) ? "Android phone" :
    /macintosh/i.test(agent) ? "Mac" :
    /windows/i.test(agent) ? "Windows PC" : "A device";
  const browser =
    /edg\//i.test(agent) ? "Edge" :
    /chrome|crios/i.test(agent) ? "Chrome" :
    /firefox|fxios/i.test(agent) ? "Firefox" :
    /safari/i.test(agent) ? "Safari" : "";
  const installed = isStandalone() ? ", installed" : "";
  return `${platform}${browser ? `, ${browser}` : ""}${installed}`;
}

////////// 3. THE TAB //////////

export default function Device() {
  return (
    <div className="space-y-5">
      <Install />
      <Notify />

      <Panel quiet className="p-5">
        <p className="eyebrow mb-2">What this does not do</p>
        <p className="hint leading-relaxed measure">
          Installing does not give this device its own login, and it does not keep order data on the
          phone. Everything is still fetched from the server each time, so a lost phone is a lost
          phone and not a lost copy of the book. Signing out here signs out everywhere it was you.
        </p>
      </Panel>
    </div>
  );
}
